import React, { useState, useRef, useEffect, useCallback } from 'react'

interface MasonryItem {
  id: number | string
  url: string
  filename: string
  aspectRatio?: number // 宽高比，用于占位
}

interface MasonryGridProps {
  items: MasonryItem[]
  columns?: number
  gap?: number
  onItemClick?: (index: number) => void
  onDelete?: (id: number) => void
  showDelete?: boolean
}

/**
 * CSS columns 实现的瀑布流布局
 * 优点：纯 CSS，性能好，天然瀑布流排列
 * 图片按原比例显示，不裁剪
 */
const MasonryGrid: React.FC<MasonryGridProps> = ({
  items,
  columns = 4,
  gap = 12,
  onItemClick,
  onDelete,
  showDelete = false,
}) => {
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set())
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())

  const handleImageLoad = useCallback((url: string) => {
    setLoadedImages(prev => new Set(prev).add(url))
  }, [])

  const handleImageError = useCallback((url: string) => {
    setFailedImages(prev => new Set(prev).add(url))
  }, [])

  // 响应式列数
  const [responsiveColumns, setResponsiveColumns] = useState(columns)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth
      if (width < 480) setResponsiveColumns(2)
      else if (width < 768) setResponsiveColumns(3)
      else if (width < 1024) setResponsiveColumns(4)
      else setResponsiveColumns(columns)
    }

    updateColumns()
    window.addEventListener('resize', updateColumns)
    return () => window.removeEventListener('resize', updateColumns)
  }, [columns])

  if (items.length === 0) return null

  return (
    <div
      ref={containerRef}
      className="masonry-grid"
      style={{
        columnCount: responsiveColumns,
        columnGap: `${gap}px`,
      }}
    >
      {items.map((item, index) => {
        const isLoading = !loadedImages.has(item.url) && !failedImages.has(item.url)
        const isFailed = failedImages.has(item.url)

        return (
          <div
            key={item.id}
            className="masonry-item break-inside-avoid mb-3"
            style={{ marginBottom: `${gap}px` }}
          >
            <div
              className="relative group rounded-xl overflow-hidden bg-gray-100 cursor-pointer shadow-sm hover:shadow-lg transition-all duration-300"
              onClick={() => onItemClick?.(index)}
            >
              {/* 加载占位 */}
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2">
                    <svg className="animate-spin h-6 w-6 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                </div>
              )}

              {/* 缩略图 */}
              {!isFailed ? (
                <img
                  src={item.url}
                  alt={item.filename}
                  className="w-full h-auto block transition-all duration-500 group-hover:scale-[1.03]"
                  loading="lazy"
                  decoding="async"
                  style={{
                    opacity: isLoading ? 0 : 1,
                    transition: 'opacity 0.4s ease, transform 0.5s ease',
                  }}
                  onLoad={() => handleImageLoad(item.url)}
                  onError={() => handleImageError(item.url)}
                  draggable={false}
                />
              ) : (
                <div className="flex items-center justify-center py-16 px-4">
                  <div className="text-center">
                    <svg className="mx-auto h-8 w-8 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-xs text-gray-400">加载失败</p>
                  </div>
                </div>
              )}

              {/* Hover 覆盖层 */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-white text-xs truncate drop-shadow-md">{item.filename}</p>
                </div>
              </div>

              {/* 放大图标 */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                <svg className="w-9 h-9 text-white opacity-0 group-hover:opacity-80 transition-opacity duration-300 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </div>

              {/* 删除按钮 */}
              {showDelete && (
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-auto">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete?.(item.id as number)
                    }}
                    className="p-1.5 bg-black/50 hover:bg-red-500 text-white rounded-full backdrop-blur-sm transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
                    aria-label="删除图片"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default MasonryGrid
